import React, { useRef, useState, ChangeEvent, DragEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ImageMarker, { Marker } from 'react-image-marker';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../../assets/icons/close-circle.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import PumpIcon from '../../../assets/icons/gas-icon.svg';
import { ReactComponent as DeviceEmptyIcon } from '../../../assets/icons/house-searching-amico.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/Button';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectField from '../../../shared-components/select-field/SelectField';
import stationManagement from '../../../utils/api/station-management';

interface StationsProperties {
  onClose: () => void;
  handleLaneMapList: () => void;
}

interface LaneValues {
  selectedDevice: string;
  siteMapFile: File | null;
  siteMapPreview: string | null;
}

interface MarkerWithDevice extends Marker {
  device: string;
  chargingPointSn: string;
  position: {
    top: number;
    left: number;
  };
}

/**
 * Add Lane Station Component
 * @param {StationsProperties} properties The component properties.
 * @returns {React.JSX.Element} The Add Lane Station component.
 */
function AddLaneStation({ onClose, handleLaneMapList }: StationsProperties) {
  const { t } = useTranslation('stationManagement');
  const navigate = useNavigate();
  const [laneValues, setLaneValues] = useState<LaneValues>({
    selectedDevice: '',
    siteMapFile: null,
    siteMapPreview: null,
  });
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const dispatch = useAppDispatch();
  const [selectDevices, setSelectDevices] = useState(false);
  const [markers, setMarkers] = useState<MarkerWithDevice[]>([]);
  const fileInputReference = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  /** All devices (you can fetch this from backend) */
  const [deviceOptions, setDeviceOptions] = useState<{ label: string; value: string }[]>([]);

  /**
   * devices list take Api
   */
  const handleDevicesList = async () => {
    setLoading(true);
    if (!selectedChargingPoint) return;
    try {
      const response = await stationManagement.fetchDeviceList({
        chargingStation: selectedChargingPoint?.id,
      });
      const formattedDevices = response.data.map((device: any) => ({
        label: `${device.chargingPointName || ''}(${device.chargingPointSn || ''})`,
        value: device.chargingPointSn || '',
      }));

      setDeviceOptions(formattedDevices);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        },
      });
    }
  };
  /** Filter out devices already assigned to markers */
  const availableDeviceOptions = deviceOptions.filter(
    (device) => !markers.some((marker) => marker.chargingPointSn === device.value),
  );

  /** Handle device selection */
  const handleChangeFunction = (name: string, value: string) => {
    setLaneValues((previous) => ({ ...previous, [name]: value }));
  };

  /** Handle file upload (input) */
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setLaneValues({
        selectedDevice: laneValues.selectedDevice,
        siteMapFile: file,
        siteMapPreview: previewUrl,
      });
      setMarkers([]);
    }
  };

  /**
   * remove for the images and devices
   */
  const handleRemoveImage = () => {
    setLaneValues({
      selectedDevice: '',
      siteMapFile: null,
      siteMapPreview: '',
    });
  };

  /** Handle drag & drop */
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setLaneValues({
        selectedDevice: laneValues.selectedDevice,
        siteMapFile: file,
        siteMapPreview: previewUrl,
      });
      setMarkers([]);
    }
  };

  /** Trigger hidden file input */
  const handleUploadClick = () => {
    fileInputReference.current?.click();
  };

  /** When new marker added on image */
  const handleAddMarker = (marker: Marker) => {
    if (!laneValues.selectedDevice) {
      setSelectDevices(true);
      return;
    }

    const selectedDevice = deviceOptions.find(
      (device) => device.value === laneValues.selectedDevice,
    );

    const top = Number(marker.top);
    const left = Number(marker.left);

    const markerWithDevice: MarkerWithDevice = {
      top,
      left,
      device: selectedDevice?.label || laneValues.selectedDevice,
      chargingPointSn: selectedDevice?.value || '',
      position: {
        top,
        left,
      },
    };

    setMarkers((previous) => [...previous, markerWithDevice]);
    setLaneValues((previous) => ({ ...previous, selectedDevice: '' }));
  };

  /**
   *handleRemoveMarker
   */
  const handleRemoveMarker = (markerToRemove: MarkerWithDevice) => {
    setMarkers((previousMarkers) =>
      previousMarkers.filter(
        (marker) =>
          marker.top !== markerToRemove.top ||
          marker.left !== markerToRemove.left ||
          marker.device !== markerToRemove.device,
      ),
    );
  };

  /** Send data to backend */
  const handleSave = async (): Promise<void> => {
    if (!selectedChargingPoint?.id) return;

    setLoadingSave(true);
    if (!laneValues.siteMapFile || !selectedChargingPoint?.id) return;

    const formData = new FormData();
    formData.append('chargingStationMap', laneValues.siteMapFile);

    markers.forEach((marker, index) => {
      formData.append(`chargingPointLocations[${index}][chargingPointSn]`, marker.chargingPointSn);
      formData.append(
        `chargingPointLocations[${index}][position][top]`,
        marker.position.top.toString(),
      );
      formData.append(
        `chargingPointLocations[${index}][position][left]`,
        marker.position.left.toString(),
      );
    });

    try {
      await stationManagement.editLaneMap(selectedChargingPoint?.id, formData);
      await handleLaneMapList();
      onClose();
      setLoadingSave(false);
    } catch (error: any) {
      setLoadingSave(false);
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        },
      });
    }
  };

  /**
   * Function for navigating to add device page
   */
  const handleNavigate = () => {
    navigate('/ev-admin/device-management/add');
  };

  useEffect(() => {
    handleDevicesList();
  }, [selectedChargingPoint]);
  useEffect(() => {
    setTimeout(() => {
      setSelectDevices(false);
    }, 3000);
  }, [selectDevices]);
  return (
    <div className="add-lane-content">
      <div className="heading">
        <PageTitle
          title={t('addLane')}
          buttonSvgIcon={<CloseIcon />}
          buttonType="none"
          onClickButton={onClose}
          buttonLabel=" "
        />
      </div>
      <div className="select-devices">
        {' '}
        <SelectField
          options={availableDeviceOptions}
          placeholder={t('selectDevice')}
          selectedValue={laneValues.selectedDevice}
          name="selectedDevice"
          onChangeValue={handleChangeFunction}
          error={selectDevices}
          errorMessage={t('selectDeviceFirst')}
        />
        <div className="add-device">
          <MyButton
            buttonType="none"
            svgIcon={<EditIcon style={{ width: '35px' }} onClick={handleRemoveImage} />}
            label=""
          />
          <MyButton
            label={t('save')}
            buttonType="submit"
            onClickFunc={handleSave}
            disableButton={!laneValues.siteMapFile}
            loading={loadingSave}
          />
        </div>
      </div>
      {loading && (
        <center>
          <BatteryLoader />
        </center>
      )}
      {deviceOptions.length > 0 && (
        <div
          className="main-container"
          onDrop={handleDrop}
          onDragOver={(event) => event.preventDefault()}>
          {laneValues.siteMapPreview ? (
            <div className="images-container">
              <ImageMarker
                src={laneValues.siteMapPreview}
                markers={markers}
                onAddMarker={handleAddMarker}
                markerComponent={(properties: Marker) => {
                  const m = properties as MarkerWithDevice;

                  return (
                    <div className="marker-wrapper">
                      <div className="device-marker">
                        <img src={PumpIcon} alt="" className="device-details-image" />
                        <p className="marker-device-label">{m.device}</p>
                        <div
                          role="button"
                          tabIndex={0}
                          className="remove-marker-button"
                          aria-label="Remove marker"
                          onClick={() => handleRemoveMarker(m)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') handleRemoveMarker(m);
                          }}>
                          <CloseIcon />
                        </div>
                      </div>

                      <div className="device-status-container online">
                        <div className="static-container" />
                        <div className="animation-container" />
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          ) : (
            <>
              <div
                className="upload-container"
                role="button"
                tabIndex={0}
                onClick={handleUploadClick}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') handleUploadClick();
                }}>
                <p className="upload">{t('dragAndDrop')}</p>
              </div>

              <div className="upload-footer">
                <p>{t('addSiteMapDesc')}</p>
                <span
                  className="upload-label"
                  role="button"
                  tabIndex={0}
                  onClick={handleUploadClick}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') handleUploadClick();
                  }}>
                  <PlusIcon /> {t('uploadSiteMap')}
                </span>

                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputReference}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </div>
            </>
          )}
        </div>
      )}
      {!loading && deviceOptions.length === 0 && (
        <div className="device-empty">
          <div className="icon">
            <DeviceEmptyIcon />
          </div>
          <div className="upload-footer">
            <p>{t('addSiteMapDesc')}</p>
            <span role="presentation" onClick={handleNavigate} className="upload-label">
              <PlusIcon />
              {t('addDevice')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddLaneStation;
